// Current connection state only. Credentials stay in the existing HttpOnly cookie.
export function connectRoom({ roomCode, onState, onStatus }) {
    let disposed = false;
    let socket = null;
    let retry = null;
    let attempt = 0;
    let heartbeatBusy = false;
    let controller = null;
    const visible = () => !disposed && document.visibilityState === 'visible' && navigator.onLine;
    const closeSocket = () => {
        if (socket) { const current = socket; socket = null; current.close(1000, 'page inactive'); }
        window.clearTimeout(retry); retry = null;
    };
    const open = () => {
        if (!visible() || socket) return;
        const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const current = new WebSocket(`${scheme}//${window.location.host}/ws/rooms/${encodeURIComponent(roomCode)}`, ['secret-digits']);
        socket = current;
        current.addEventListener('open', () => { if (socket === current && !disposed) { attempt = 0; onStatus('subscribed'); onState(); } });
        current.addEventListener('message', () => { if (!disposed) onState(); });
        current.addEventListener('error', () => { if (!disposed) onStatus('fallback'); current.close(); });
        current.addEventListener('close', () => {
            if (socket !== current || disposed) return;
            socket = null; onStatus('fallback');
            if (visible()) retry = window.setTimeout(open, Math.min(8000, 1000 * 2 ** Math.min(attempt++, 3)));
        });
    };
    const heartbeat = async () => {
        if (!visible() || heartbeatBusy) return;
        heartbeatBusy = true;
        controller = new AbortController();
        const timeout = window.setTimeout(() => controller?.abort(), 8000);
        try {
            const response = await fetch(`/api/v1/rooms/${encodeURIComponent(roomCode)}/presence`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Secret-Digits-Web': '1' }, body: '{}', signal: controller.signal });
            if (!response.ok) throw new Error('Connection unavailable');
            const result = await response.json();
            if (disposed) return;
            if (!result.accepted) { closeSocket(); onState(); return; }
            if (!socket) open();
        } catch { if (!disposed) { closeSocket(); onStatus('fallback'); } }
        finally { window.clearTimeout(timeout); controller = null; heartbeatBusy = false; }
    };
    const resume = () => {
        if (visible()) { void heartbeat(); onState(); }
        else { closeSocket(); onStatus('fallback'); }
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', resume);
    window.addEventListener('offline', resume);
    window.addEventListener('pageshow', resume);
    const interval = window.setInterval(heartbeat, 15000);
    resume();
    return () => {
        disposed = true; closeSocket(); controller?.abort(); window.clearInterval(interval);
        document.removeEventListener('visibilitychange', resume);
        for (const event of ['online', 'offline', 'pageshow']) window.removeEventListener(event, resume);
    };
}
